import type {LinkField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextLink} from '@prismicio/next/pages'
import cn from 'classnames'
import type {LinkProps} from 'next/link'
import {useLinkResolver} from 'prismicio'
import {memo} from 'react'
import type {TrackingProps} from 'src/common/helpers'
import {EVENTS, track} from 'src/common/helpers'

type TextLinkProps = {
  label?: string
  variation?: TextLinkVariations
  disabled?: boolean
  underline?: boolean
  target?: string
  field?: LinkField
  fontWeight?: 'font-medium' | 'font-semibold'
  color?:
    | 'moss-black'
    | 'neon-550'
    | 'accent-neon'
    | 'moss-white'
    | 'green-neon'
    | 'moss-blue'
    | 'moss-grey-550'
    | 'moss-grey-400'
  ignoreLocale?: boolean
  className?: string
  arrowFirst?: boolean
} & Omit<LinkProps, 'href'> &
  Omit<TrackingProps, 'event'>

type TextLinkVariations = 'normal' | 'small' | 'x-small' | 'responsive'

const TextLinkComponent = ({
  label,
  variation = 'normal',
  underline = false,
  disabled = false,
  mktgId,
  pageName,
  location,
  dataType,
  field,
  color = 'moss-black',
  fontWeight = 'font-medium',
  ignoreLocale = false,
  className: classNameOverride,
  arrowFirst = false,
  target,
}: TextLinkProps) => {
  const {linkResolver} = useLinkResolver()
  const isLinkToDoc = isFilled.link(field) && field.link_type === 'Document'

  const getLocale = () => {
    if (ignoreLocale) {
      return {locale: false as const}
    }

    if (isLinkToDoc) {
      return {locale: field.lang}
    }

    return {}
  }

  if (!label) {
    return null
  }

  return (
    <div className="isolate inline-flex">
      <PrismicNextLink
        href={linkResolver(field)}
        {...getLocale()}
        {...(target ? {target, rel: 'noopener noreferrer'} : {})}
        className={cn(
          `group/textlink w-fit gap-1.5 ${fontWeight} tracking-[.28px] focus:border-2 focus:border-blue-550`,
          {
            'text-body-xs lg:text-body-s': variation === 'x-small',
            'text-button-s leading-[14px]': variation === 'normal',
            'text-body-bold-s': variation === 'small',
            'text-14 leading-5 lg:text-16 lg:leading-6': variation === 'responsive',
            'text-grey-200 pointer-events-none': disabled,
            'bg-text-link bg-[100%_1px,0_1px] bg-no-repeat hover:bg-[0_1px,100%_1px] transition-all duration-700 w-max pb-1 [background-position:100%_100%,0_100%]':
              underline,
            'text-moss-black': color === 'moss-black',
            'text-neon-550': color === 'neon-550',
            'text-accent-neon': color === 'accent-neon',
            'text-moss-white': color === 'moss-white',
            'text-green-neon': color === 'green-neon',
            'text-moss-blue': color === 'moss-blue',
            'text-moss-grey-550': color === 'moss-grey-550',
            'text-moss-grey-400': color === 'moss-grey-400',
          },
          classNameOverride,
        )}
        onClick={() => {
          track({
            event: EVENTS.CLICK,
            pageName,
            location,
            dataType,
            label,
            mktgId,
          })
        }}
        aria-disabled={disabled}
      >
        {!underline && arrowFirst && <LinkArrow position="before" />}
        {label}
        {!underline && !arrowFirst && <LinkArrow />}
      </PrismicNextLink>
    </div>
  )
}

export const LinkArrow = ({position = 'after'}: {position?: 'before' | 'after'}) => (
  <span
    aria-hidden="true"
    className={cn('inline-block duration-200 group-hover/textlink:translate-x-[3px] group-hover:ease-in-out', {
      'pr-1': position === 'before',
      'pl-1.5': position === 'after',
    })}
  >
    →
  </span>
)

export const TextLink = memo(TextLinkComponent)
